import React from 'react';
import { View, Text, Pressable, Platform, ActivityIndicator } from 'react-native';
import { WEB_HOVER_TRANSITION_STYLE } from '../../src/shared/constants/animations';
import { AlarmClock } from 'lucide-react-native';
import {
  challengeCardStyles as defaultStyles,
  type ChallengeCardStyles,
} from '../../src/challenges/styles/challengeCard.styles';
import { InfoTooltip } from '../../src/shared/components/InfoTooltip';
import { CHALLENGE_REMIND_BUTTON_TOOLTIP } from '../../constants/tooltips';
import { useTheme } from '../../src/theme/useTheme';

interface ChallengeCardActionsProps {
  /** Theme-aware styles from parent. When omitted, light theme styles are used. */
  styles?: ChallengeCardStyles;
  canCancelChallenge: boolean;
  isPendingParticipant: boolean;
  insufficientCoins?: boolean;
  hasChanges: boolean;
  isUpdating: boolean;
  /** When true, first game has started — accept/update is no longer allowed. */
  isFirstGameStarted?: boolean;
  onCancelChallenge: () => void;
  onDeclineChallenge: () => void;
  /** When set, main CTA opens the picks modal instead of updating inline. */
  onOpenPicksModal?: () => void;
  onUpdatePredictions: () => void;
  /** When set, show Remind (alarm) button + tooltip beside the Update button (waiting-for-friends). */
  onRemindPress?: () => void;
  /** When true, Remind button shows loading state (clicked, waiting for response). */
  isReminding?: boolean;
  /** Overrides main CTA label (e.g. guest pending Monkey card → sign up). */
  primaryCtaLabelOverride?: string;
  /** Test ID for testing */
  testID?: string;
}

export const ChallengeCardActions: React.FC<ChallengeCardActionsProps> = ({
  styles = defaultStyles,
  canCancelChallenge,
  isPendingParticipant,
  insufficientCoins = false,
  hasChanges,
  isUpdating,
  isFirstGameStarted = false,
  onCancelChallenge,
  onDeclineChallenge,
  onOpenPicksModal,
  onUpdatePredictions,
  onRemindPress,
  isReminding = false,
  primaryCtaLabelOverride,
  testID,
}) => {
  const theme = useTheme();
  const useModal = !!onOpenPicksModal;
  const mainCtaDisabled =
    isFirstGameStarted || insufficientCoins || (useModal ? isUpdating : !hasChanges || isUpdating);
  const mainCtaPress = useModal ? onOpenPicksModal : onUpdatePredictions;
  const white = theme?.white ?? '#FFFFFF';

  return (
    <View style={styles.compactFooter} testID={testID || 'challenge-card-actions'}>
      {/* Cancel challenge: creator only, PENDING only; confirmation dialog in parent */}
      {canCancelChallenge && onCancelChallenge && (
        <Pressable
          style={({ pressed, hovered }) => [
            styles.compactActionButton,
            styles.compactCancelButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.compactActionButtonPressed,
            hovered && Platform.OS === 'web' && styles.compactActionButtonHover,
          ]}
          onPress={(e) => {
            e?.stopPropagation?.();
            onCancelChallenge();
          }}
          testID={testID ? `${testID}-cancel-button` : 'challenge-card-cancel-button'}
        >
          <Text style={[styles.compactActionText, styles.compactCancelText]}>
            Cancel
          </Text>
        </Pressable>
      )}
      {onRemindPress && (
        <InfoTooltip
          content={CHALLENGE_REMIND_BUTTON_TOOLTIP}
          accessibilityLabel="What does the Remind button do?"
          tooltipOnlyOnHover
          trigger={
            <Pressable
              style={({ pressed, hovered }) => [
                styles.cardRemindButton,
                isReminding && styles.cardRemindButtonDisabled,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                !isReminding && pressed && styles.cardRemindButtonPressed,
                !isReminding && hovered && Platform.OS === 'web' && styles.cardRemindButtonHover,
              ]}
              onPress={(e) => {
                e?.stopPropagation?.();
                if (!isReminding) onRemindPress();
              }}
              disabled={isReminding}
              testID={testID ? `${testID}-remind-button` : 'challenge-card-remind-button'}
            >
              {isReminding ? (
                <ActivityIndicator size="small" color={white} />
              ) : (
                <AlarmClock size={20} color={white} />
              )}
            </Pressable>
          }
        />
      )}
      {/* Decline button for pending participants */}
      {isPendingParticipant && (
        <Pressable
          style={({ pressed, hovered }) => [
            styles.compactActionButton,
            styles.compactDeclineButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.compactActionButtonPressed,
            hovered && Platform.OS === 'web' && styles.compactActionButtonHover,
          ]}
          onPress={onDeclineChallenge}
          testID={testID ? `${testID}-decline-button` : 'challenge-card-decline-button'}
        >
          <Text
            style={[styles.compactActionText, styles.compactDeclineText]}
          >
            Decline
          </Text>
        </Pressable>
      )}
      {/* Main CTA: open picks modal or update predictions inline */}
      <Pressable
        style={({ pressed, hovered }) => [
          styles.compactActionButton,
          mainCtaDisabled && styles.compactActionButtonDisabled,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          !mainCtaDisabled && pressed && styles.compactActionButtonPressed,
          !mainCtaDisabled && hovered && Platform.OS === 'web' && styles.compactActionButtonHover,
        ]}
        onPress={mainCtaPress}
        disabled={mainCtaDisabled}
        testID={testID ? `${testID}-update-button` : 'challenge-card-update-button'}
      >
        <Text
          style={[
            styles.compactActionText,
            mainCtaDisabled && styles.compactActionTextDisabled,
          ]}
        >
          {isUpdating
            ? 'Updating...'
            : primaryCtaLabelOverride
            ? primaryCtaLabelOverride
            : isFirstGameStarted
            ? 'Started'
            : insufficientCoins
            ? 'Not Enough Coins'
            : useModal
            ? isPendingParticipant
              ? 'Accept'
              : 'Update'
            : hasChanges
            ? 'Update'
            : isPendingParticipant
            ? 'Accept'
            : 'No Changes'}
        </Text>
      </Pressable>
    </View>
  );
};

